<template>
    <v-dialog
        ref="dialog"
        v-model="isAuthDialog"
        fullscreen
        hide-overlay
        :persistent="isMarketplaceInitLocked"
        transition="fade-transition"
        content-class="dh-auth-dialog-root"
    >
        <div class="auth-backdrop" @mousedown.self="onBackdropMouseDown" @click.self="onBackdropClick">
            <section
                class="auth-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="authDialogTitle"
            >
                <v-overlay :absolute="true" :value="loading" opacity="0.55" z-index="10">
                    <SpinnerLoader />
                </v-overlay>

                <button
                    v-if="!isMarketplaceInitLocked"
                    type="button"
                    class="auth-close"
                    aria-label="Закрыть окно авторизации"
                    @click="cancel"
                >
                    <v-icon size="18">mdi-close</v-icon>
                </button>

                <div class="auth-hero">
                    <div class="auth-brand-row" aria-label="Личный кабинет Data Heroes">
                        <div class="auth-brand-copy">
                            <span class="auth-brand-caption">Личный кабинет</span>
                            <img class="auth-brand-logo" :src="logoSrc" alt="Data Heroes" />
                        </div>
                    </div>
                    <div
                        class="auth-hero-title"
                        :class="{ 'auth-hero-title--promo': isPromoHero }"
                    >
                        {{ heroCopy.title }}
                    </div>
                    <div v-if="heroCopy.sub" class="auth-hero-sub">{{ heroCopy.sub }}</div>
                    <div
                        v-if="activeSection !== 'passwordReset'"
                        class="auth-tabs"
                        role="tablist"
                        aria-label="Авторизация Data Heroes"
                    >
                        <button
                            v-for="tab in tabs"
                            :key="tab.id"
                            type="button"
                            class="auth-tab"
                            :class="{ 'is-active': activeSection === tab.id }"
                            role="tab"
                            :aria-selected="activeSection === tab.id"
                            @click="setSection(tab.id)"
                        >
                            {{ tab.label }}
                        </button>
                    </div>
                </div>

                <h2 id="authDialogTitle" class="sr-only">Вход и регистрация Data Heroes</h2>

                <div class="auth-body">
                    <!-- LOGIN -->
                    <div v-if="activeSection === 'login'" class="auth-panel">
                        <form class="auth-form" novalidate @submit.prevent="login">
                            <div class="auth-fields">
                                <div>
                                    <label class="auth-field" :class="{ 'is-invalid': fieldErrors.email }">
                                        <v-icon size="20">mdi-email</v-icon>
                                        <input
                                            v-model.trim="credentials.email"
                                            type="email"
                                            name="email"
                                            placeholder="Электронная почта"
                                            autocomplete="email"
                                            required
                                            @input="clearFieldError('email')"
                                        />
                                    </label>
                                    <span v-if="fieldErrors.email" class="auth-error">{{ fieldErrors.email }}</span>
                                </div>
                                <div>
                                    <label class="auth-field" :class="{ 'is-invalid': fieldErrors.password }">
                                        <v-icon size="20">mdi-lock</v-icon>
                                        <input
                                            v-model="credentials.password"
                                            :type="showPassword ? 'text' : 'password'"
                                            name="password"
                                            placeholder="Пароль"
                                            autocomplete="current-password"
                                            required
                                            @input="onPasswordInput"
                                        />
                                        <button
                                            type="button"
                                            class="auth-eye"
                                            :aria-label="showPassword ? 'Скрыть пароль' : 'Показать пароль'"
                                            @click="showPassword = !showPassword"
                                        >
                                            <v-icon size="20">{{
                                                showPassword ? 'mdi-eye-off' : 'mdi-eye'
                                            }}</v-icon>
                                        </button>
                                    </label>
                                    <span v-if="fieldErrors.password" class="auth-error">{{
                                        fieldErrors.password
                                    }}</span>
                                </div>
                            </div>
                            <button type="button" class="auth-link" @click="setSection('passwordReset')">
                                Не помню пароль
                            </button>
                            <div class="auth-submit-row">
                                <button type="submit" class="btn-indigo auth-submit">Войти</button>
                                <span class="auth-helper">Вход только по почте и паролю.</span>
                            </div>
                        </form>
                    </div>

                    <!-- REGISTER -->
                    <div v-else-if="activeSection === 'register'" class="auth-panel">
                        <form class="auth-form" novalidate @submit.prevent="register">
                            <div class="auth-fields">
                                <div class="auth-field-group">
                                    <span class="auth-field-label">Почта</span>
                                    <label class="auth-field" :class="{ 'is-invalid': fieldErrors.email }">
                                        <v-icon size="20">mdi-email</v-icon>
                                        <input
                                            v-model.trim="credentials.email"
                                            type="email"
                                            name="email"
                                            placeholder="Электронная почта"
                                            autocomplete="email"
                                            required
                                            @input="clearFieldError('email')"
                                        />
                                    </label>
                                    <span v-if="fieldErrors.email" class="auth-error">{{ fieldErrors.email }}</span>
                                </div>
                                <div class="auth-field-group">
                                    <span class="auth-field-label">Пароль</span>
                                    <label class="auth-field" :class="{ 'is-invalid': fieldErrors.password }">
                                        <v-icon size="20">mdi-lock</v-icon>
                                        <input
                                            v-model="credentials.password"
                                            :type="showPassword ? 'text' : 'password'"
                                            name="password"
                                            placeholder="Придумайте пароль"
                                            autocomplete="new-password"
                                            required
                                            @input="onPasswordInput"
                                        />
                                        <button
                                            type="button"
                                            class="auth-eye"
                                            :aria-label="showPassword ? 'Скрыть пароль' : 'Показать пароль'"
                                            @click="showPassword = !showPassword"
                                        >
                                            <v-icon size="20">{{
                                                showPassword ? 'mdi-eye-off' : 'mdi-eye'
                                            }}</v-icon>
                                        </button>
                                    </label>
                                    <span v-if="fieldErrors.password" class="auth-error">{{
                                        fieldErrors.password
                                    }}</span>
                                </div>
                            </div>

                            <div class="auth-consent">
                                <div class="auth-consent-title">
                                    Я даю согласие ООО «ДАТА ХИРОУС» и<br />ООО «ДАТА ХИРОУС ТЕХ»:
                                </div>
                                <label class="auth-check" :class="{ 'is-invalid': fieldErrors.consent }">
                                    <input
                                        v-model="personalDataConsent"
                                        type="checkbox"
                                        name="personal_data_consent"
                                        required
                                        @change="clearFieldError('consent')"
                                    />
                                    <span>
                                        <a
                                            href="https://dataheroes.pro/policy-two"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            @click.stop
                                            >На обработку персональных данных</a
                                        >
                                    </span>
                                </label>
                                <span v-if="fieldErrors.consent" class="auth-error auth-error--check">{{
                                    fieldErrors.consent
                                }}</span>
                            </div>

                            <div class="auth-submit-row auth-submit-row-full">
                                <button type="submit" class="btn-indigo auth-submit auth-submit-wide">
                                    Зарегистрироваться
                                </button>
                            </div>
                            <p class="auth-legal-fine">
                                Нажимая на кнопку, я соглашаюсь с условиями
                                <a href="https://dataheroes.pro/terms" target="_blank" rel="noopener noreferrer"
                                    >Пользовательского соглашения</a
                                >
                                и
                                <a href="https://dataheroes.pro/policy-two" target="_blank" rel="noopener noreferrer"
                                    >Политики конфиденциальности</a
                                >.
                            </p>
                        </form>
                    </div>

                    <!-- PASSWORD RESET -->
                    <div v-else class="auth-panel">
                        <form class="auth-form" novalidate @submit.prevent="resetPassword">
                            <div>
                                <label class="auth-field" :class="{ 'is-invalid': fieldErrors.email }">
                                    <v-icon size="20">mdi-email</v-icon>
                                    <input
                                        v-model.trim="credentials.email"
                                        type="email"
                                        name="email"
                                        placeholder="Электронная почта"
                                        autocomplete="email"
                                        required
                                        @input="clearFieldError('email')"
                                    />
                                </label>
                                <span v-if="fieldErrors.email" class="auth-error">{{ fieldErrors.email }}</span>
                            </div>
                            <div class="auth-submit-row auth-submit-row-full">
                                <button type="submit" class="btn-indigo auth-submit auth-submit-wide">
                                    Отправить ссылку
                                </button>
                                <button type="button" class="auth-back" @click="setSection('login')">
                                    <v-icon size="18">mdi-arrow-left</v-icon>
                                    Вернуться ко входу
                                </button>
                            </div>
                            <div class="auth-message" :class="{ 'is-visible': passwordReseted }">
                                Ссылка для восстановления будет отправлена на почту.
                            </div>
                        </form>
                    </div>

                    <div
                        v-if="activeSection !== 'register'"
                        class="auth-google-note"
                        role="note"
                        aria-label="Изменение способа входа через Google"
                    >
                        <span class="auth-google-note-icon" aria-hidden="true">!</span>
                        <p class="auth-google-note-text">
                            <strong>Кнопка «Войти через Google» больше недоступна.</strong>
                            Почту Gmail можно использовать. Если раньше Вы входили через Google, нажмите
                            <a href="#" @click.prevent="setSection('passwordReset')">Не помню пароль</a>, создайте
                            пароль и затем входите по адресу электронной почты.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    </v-dialog>
</template>

<script>
import SpinnerLoader from '../SpinnerLoader.vue'
import { isMarketplaceInitPath } from '@/helpers/promo/session'

const HERO_COPY = {
    login: {
        title: 'Вход в систему роста выручки',
        sub: 'Рассылки, сегменты, каскады, аналитика и KPI сотрудников в одном кабинете.',
    },
    register: {
        title: 'Регистрация',
        sub: 'Укажите почту и придумайте пароль — вход будет доступен сразу после создания кабинета.',
    },
    passwordReset: {
        title: 'Восстановление пароля',
        sub: 'Введите email, и мы отправим инструкции по восстановлению доступа.',
    },
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
    name: 'AuthDialog',
    components: { SpinnerLoader },
    data: () => ({
        logoSrc: require('@/assets/Logo_light.svg'),
        credentials: {
            email: '',
            password: '',
        },
        personalDataConsent: false,
        loading: false,
        utmSource: null,
        showPassword: false,
        activeSection: 'login',
        error: null,
        fieldErrors: {},
        passwordReseted: false,
        backdropMouseDownOutside: false,
        tabs: [
            { id: 'login', label: 'Вход' },
            { id: 'register', label: 'Регистрация' },
        ],
    }),
    computed: {
        isAuthDialog: {
            get() {
                return this.$store.state.isAuthDialog
            },
            set(value) {
                this.$store.commit('setIsAuthDialog', value)
            },
        },
        authDialogStartSection: {
            get() {
                return this.$store.state.authDialogStartSection
            },
            set(value) {
                this.$store.commit('setAuthDialogStartSection', value)
            },
        },
        isPromoAuth() {
            return this.$route.name === 'PromoResolve'
        },
        isPromoHero() {
            return this.isPromoAuth && (this.activeSection === 'login' || this.activeSection === 'register')
        },
        isMarketplaceInitLocked() {
            return !this.$auth.isAuthenticated && isMarketplaceInitPath(this.$route.path)
        },
        heroCopy() {
            if (this.isPromoHero) {
                return {
                    title: 'Чтобы воспользоваться промопредложением, вам нужно войти в личный кабинет',
                    sub: '',
                }
            }
            return HERO_COPY[this.activeSection] || HERO_COPY.login
        },
    },
    watch: {
        isAuthDialog(val) {
            if (val) {
                this.activeSection = this.authDialogStartSection || 'login'
                this.init()
            } else {
                this.backdropMouseDownOutside = false
            }
        },
        activeSection() {
            this.clearErrors()
            this.showPassword = false
            this.passwordReseted = false
        },
    },
    methods: {
        setSection(section) {
            this.activeSection = section
        },
        clearFieldError(field) {
            if (!this.fieldErrors[field]) return
            const next = { ...this.fieldErrors }
            delete next[field]
            this.fieldErrors = next
            this.error = null
        },
        onPasswordInput(event) {
            const cleaned = String(event.target.value || '').replace(/\s/g, '')
            this.credentials.password = cleaned
            if (event.target.value !== cleaned) {
                event.target.value = cleaned
            }
            this.clearFieldError('password')
        },
        clearErrors() {
            this.fieldErrors = {}
            this.error = null
        },
        validateEmailPassword({ requirePassword = true } = {}) {
            const errors = {}
            const email = (this.credentials.email || '').trim()
            const password = this.credentials.password || ''

            if (!email) {
                errors.email = 'Укажите адрес электронной почты.'
            } else if (!EMAIL_RE.test(email)) {
                errors.email = 'Проверьте формат электронной почты.'
            }

            if (requirePassword) {
                if (!password.trim()) {
                    errors.password = 'Заполните это поле.'
                }
            }

            return errors
        },
        validateLogin() {
            return this.validateEmailPassword({ requirePassword: true })
        },
        validateRegister() {
            const errors = this.validateEmailPassword({ requirePassword: true })
            if (!this.personalDataConsent) {
                errors.consent = 'Подтвердите согласие на обработку персональных данных.'
            }
            return errors
        },
        validateReset() {
            return this.validateEmailPassword({ requirePassword: false })
        },
        applyFieldErrors(errors) {
            this.fieldErrors = errors
            this.error = null
            return Object.keys(errors).length === 0
        },
        setApiFieldError(field, message) {
            this.fieldErrors = { [field]: message }
            this.error = null
        },
        async login() {
            if (!this.applyFieldErrors(this.validateLogin())) return

            this.loading = true
            const { email, password } = this.credentials
            const { error } = await this.$auth.login(email, password)
            if (error) {
                this.setApiFieldError('password', error.message)
                this.loading = false
                return
            }
            this.$store.commit('setOpenCreateProjectAfterRegister', false)
            this.$store.commit('setIsAuthDialog', false)
            this.loading = false
        },
        async register() {
            if (!this.applyFieldErrors(this.validateRegister())) return

            this.loading = true
            const { email, password } = this.credentials
            const { error: error1 } = await this.$auth.register(email, password, this.utmSource)
            if (error1) {
                this.setApiFieldError('email', error1.message)
                this.loading = false
                return
            }
            const { error: error2 } = await this.$auth.login(email, password)
            if (error2) {
                this.setApiFieldError('password', error2.message)
                this.loading = false
                return
            }
            this.$store.commit('setIsAuthDialog', false)
            this.loading = false
        },
        async resetPassword() {
            if (!this.applyFieldErrors(this.validateReset())) return

            this.loading = true
            const { email } = this.credentials
            const { error } = await this.$auth.resetPassword(email)
            if (error) {
                this.setApiFieldError('email', error.message)
                this.loading = false
                return
            }
            this.passwordReseted = true
            this.loading = false
        },
        cancel() {
            if (this.isMarketplaceInitLocked) return
            this.$store.commit('setOpenCreateProjectAfterRegister', false)
            this.isAuthDialog = false
            this.init()
        },
        onBackdropMouseDown() {
            this.backdropMouseDownOutside = true
        },
        onBackdropClick() {
            if (this.isMarketplaceInitLocked) return
            if (!this.backdropMouseDownOutside) return
            this.cancel()
        },
        init() {
            const prefillEmail = this.$store.state.authDialogPrefillEmail || ''
            this.credentials = { email: prefillEmail, password: '' }
            this.$store.commit('setAuthDialogPrefillEmail', null)
            this.personalDataConsent = false
            this.showPassword = false
            this.passwordReseted = false
            this.clearErrors()
        },
    },
    created() {
        this.$router.onReady(() => {
            const query = this.$router.currentRoute.query || {}
            Object.entries(query).forEach(([key, value]) => {
                if (!String(key).startsWith('utm_')) return
                const normalizedValue = Array.isArray(value) ? value.find(Boolean) : value
                if (!normalizedValue) return
                localStorage.setItem(key, String(normalizedValue))
            })
            this.utmSource = localStorage.getItem('utm_source')
        })
    },
}
</script>

<style lang="sass">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap')
@import '@/vars.sass'

.dh-auth-dialog-root
    box-shadow: none !important
    background: transparent !important
    height: 100% !important
    max-height: 100% !important
    margin: 0 !important
    overflow: visible !important

.auth-backdrop
    --ink: #{$dark-new}
    --indigo: #{$main-new}
    --indigo-d: #454CC0
    --wasabi: #{$pop-new}
    --danger: #{$alert-new}
    --indigo-tint: #{$bcg-card-new}
    --line: #{$border-new}
    --line2: #{$border-light-new}
    --text: #{$text-color-new}
    --mut: #{$muted-new}
    --surface: #FFFFFF

    position: fixed
    inset: 0
    z-index: 90
    background: radial-gradient(circle at 52% 18%, rgba($main-new, .34), transparent 34%), rgba($dark-new, .66)
    backdrop-filter: blur(16px)
    display: flex
    align-items: center
    justify-content: center
    padding: 24px
    font-family: 'Inter', system-ui, sans-serif
    color: var(--text)
    -webkit-font-smoothing: antialiased

.auth-card
    position: relative
    width: min(448px, 100%)
    max-height: calc(100dvh - 48px)
    background: var(--surface)
    border: 1px solid rgba(255, 255, 255, .6)
    border-radius: 26px
    box-shadow: 0 32px 88px rgba(11, 14, 34, .42)
    overflow: hidden
    color: var(--text)
    display: flex
    flex-direction: column

    &::after
        content: ''
        position: absolute
        right: -96px
        top: -96px
        width: 230px
        height: 230px
        border-radius: 50%
        background: radial-gradient(circle, rgba($pop-new, .42), transparent 66%)
        filter: blur(8px)
        pointer-events: none

.auth-close
    position: absolute
    right: 16px
    top: 16px
    width: 36px
    height: 36px
    border-radius: 12px
    border: 1px solid rgba(255, 255, 255, .22)
    background: rgba(255, 255, 255, .1)
    color: #fff
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer
    box-shadow: none
    z-index: 5
    transition: transform .18s, color .18s, background .18s, border-color .18s

    &:hover
        transform: translateY(-1px)
        color: #fff
        background: rgba(255, 255, 255, .18)
        border-color: rgba(255, 255, 255, .38)

    .v-icon
        color: #fff !important

.auth-hero
    position: relative
    z-index: 1
    flex: 0 0 auto
    padding: 24px 24px 16px
    background: linear-gradient(135deg, var(--ink) 0%, #23254E 58%, #30365F 100%)
    color: #fff !important
    overflow: hidden

    &::before
        content: ''
        position: absolute
        inset: 0
        z-index: 0
        background: radial-gradient(circle at 62% 15%, rgba(255, 255, 255, .9) 0 1px, transparent 2px), radial-gradient(circle at 82% 27%, rgba($pop-new, .25) 0 5px, transparent 7px), radial-gradient(circle at 88% 62%, rgba(124, 131, 232, .55) 0 1.5px, transparent 3px), radial-gradient(circle at 72% 58%, rgba(255, 255, 255, .15) 0 22px, transparent 23px), repeating-linear-gradient(158deg, transparent 0 17px, rgba(124, 131, 232, .075) 18px 19px), linear-gradient(128deg, rgba($main-new, .18), transparent 46%)
        pointer-events: none

    &::after
        content: ''
        position: absolute
        right: -72px
        top: 28px
        z-index: 0
        width: 280px
        height: 178px
        border-radius: 50%
        border: 1.5px solid rgba($pop-new, .18)
        border-left-color: transparent
        border-bottom-color: transparent
        transform: rotate(-24deg)
        box-shadow: inset 0 0 0 1px rgba(124, 131, 232, .08), 0 0 48px rgba($main-new, .18)
        pointer-events: none

    > *
        position: relative
        z-index: 2
        color: inherit

.auth-brand-row
    display: flex
    align-items: center
    gap: 12px
    padding-right: 44px
    min-height: 54px

.auth-brand-copy
    min-width: 0
    display: flex
    flex-direction: column
    gap: 5px

.auth-brand-caption
    font-family: 'Geologica', sans-serif
    font-size: 10px
    font-weight: 900
    letter-spacing: .09em
    text-transform: uppercase
    color: $pop-new !important

.auth-brand-logo
    display: block
    width: 150px
    height: auto
    max-height: 44px
    object-fit: contain
    object-position: left center

.auth-hero-title
    margin-top: 18px
    max-width: 330px
    font-family: 'Geologica', sans-serif
    font-size: 24px
    font-weight: 900
    line-height: 1.08
    letter-spacing: -.045em
    color: #ffffff !important

.auth-hero-title--promo
    max-width: none
    font-size: 20px
    font-weight: 800
    line-height: 1.25
    letter-spacing: -.03em

.auth-hero-sub
    margin-top: 8px
    max-width: 360px
    color: rgba(255, 255, 255, .72) !important
    font-size: 12.8px
    line-height: 1.5

.auth-tabs
    height: 44px
    margin-top: 16px
    padding: 4px
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 4px
    background: rgba(255, 255, 255, .09)
    border: 1px solid rgba(255, 255, 255, .13)
    border-radius: 14px
    overflow: hidden
    flex: 0 0 auto

.auth-tab
    border: 0
    border-radius: 10px
    background: transparent
    color: rgba(255, 255, 255, .68) !important
    font-family: 'Geologica', sans-serif
    font-size: 12px
    font-weight: 900
    letter-spacing: .04em
    text-transform: uppercase
    cursor: pointer
    transition: background .16s ease, color .16s ease, box-shadow .16s ease

    &:hover
        color: #fff !important
        background: rgba(255, 255, 255, .08)

    &.is-active
        background: #fff
        color: $main-new !important
        box-shadow: 0 10px 24px rgba(0, 0, 0, .16)

.sr-only
    position: absolute
    width: 1px
    height: 1px
    padding: 0
    margin: -1px
    overflow: hidden
    clip: rect(0, 0, 0, 0)
    white-space: nowrap
    border: 0

.auth-body
    padding: 24px
    overflow: auto
    min-height: 0
    background: linear-gradient(180deg, #fff 0%, #FAFBFF 100%)

.auth-form
    display: flex
    flex-direction: column
    gap: 18px

.auth-fields
    display: flex
    flex-direction: column
    gap: 12px

.auth-field-group
    display: flex
    flex-direction: column
    gap: 7px

.auth-field-label
    font-size: 12px
    font-weight: 700
    color: var(--mut)
    line-height: 1.2

.auth-field
    height: 54px
    background: #fff
    border: 1.5px solid var(--line)
    border-radius: 16px
    display: flex
    align-items: center
    gap: 12px
    padding: 0 14px
    transition: border-color .16s, box-shadow .16s, background .16s, transform .16s
    box-shadow: 0 8px 20px rgba($dark-new, .04)

    &:hover
        border-color: rgba($main-new, .24)

    &:focus-within
        background: #fff
        border-color: var(--indigo)
        box-shadow: 0 0 0 4px rgba($main-new, .11), 0 12px 26px rgba($main-new, .09)
        transform: translateY(-1px)

    &.is-invalid
        border-color: var(--danger)
        box-shadow: 0 0 0 4px rgba($alert-new, .10)

    .v-icon
        color: #6E7279 !important
        flex-shrink: 0

    input
        width: 100%
        min-width: 0
        height: 100%
        border: 0
        outline: 0
        background: transparent
        color: #252A3B
        font-family: 'Inter', system-ui, sans-serif
        font-size: 14px
        font-weight: 650

        &::placeholder
            color: #7A839A
            font-weight: 600

.auth-eye
    width: 30px
    height: 30px
    border: 0
    background: transparent
    color: #6E7279
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer
    flex-shrink: 0
    border-radius: 10px

    &:hover
        background: rgba($main-new, .09)
        color: var(--indigo)

        .v-icon
            color: $main-new !important

.auth-link
    align-self: flex-end
    font-size: 13px
    font-weight: 800
    color: $main-new !important
    background: none
    border: 0
    padding: 0
    cursor: pointer

    &:hover
        color: darken($main-new, 8%) !important
        text-decoration: underline
        text-underline-offset: 3px

.auth-submit-row
    display: flex
    align-items: center
    gap: 12px
    flex-wrap: wrap

.auth-submit-row-full
    width: 100%
    display: flex
    flex-direction: column
    align-items: stretch
    gap: 10px

.btn-indigo
    display: inline-flex
    align-items: center
    justify-content: center
    gap: 8px
    border: none
    cursor: pointer
    font-family: 'Geologica', sans-serif
    font-weight: 800
    letter-spacing: -.01em
    white-space: nowrap
    transition: background .18s, transform .18s

.auth-submit
    min-height: 44px
    padding: 12px 20px
    border-radius: 14px
    background: var(--indigo)
    color: #fff
    box-shadow: 0 8px 22px rgba($main-new, .30)
    font-size: 13px
    text-transform: uppercase
    letter-spacing: .03em

    &:hover
        background: var(--indigo-d)

.auth-submit-wide
    width: 100%
    min-height: 48px
    text-transform: none
    font-size: 14px
    letter-spacing: -.01em

.auth-helper
    font-size: 12px
    line-height: 1.5
    color: var(--mut)
    max-width: 280px

.auth-consent
    display: flex
    flex-direction: column
    gap: 10px
    padding: 4px 0 0

.auth-consent-title
    font-size: 12.5px
    line-height: 1.45
    color: #5D6578
    font-weight: 600

.auth-check
    display: grid
    grid-template-columns: 18px 1fr
    gap: 10px
    align-items: start
    color: var(--text)
    font-size: 13px
    font-weight: 650
    line-height: 1.45
    cursor: pointer

    input
        appearance: none
        width: 18px
        height: 18px
        border-radius: 5px
        border: 1.5px solid #D6DAE6
        background: #fff
        margin-top: 1px
        display: grid
        place-items: center
        cursor: pointer
        transition: background .15s, border-color .15s, box-shadow .15s

        &:checked
            background: var(--wasabi)
            border-color: var(--wasabi)
            box-shadow: 0 0 0 3px rgba($pop-new, .22)

            &::after
                content: ''
                width: 9px
                height: 5px
                border-left: 2px solid var(--ink)
                border-bottom: 2px solid var(--ink)
                transform: rotate(-45deg) translateY(-1px)

    &.is-invalid input
        border-color: var(--danger)
        box-shadow: 0 0 0 3px rgba($alert-new, .10)

    a
        color: #174AEF
        text-decoration: underline
        text-underline-offset: 3px
        text-decoration-thickness: 1px
        font-weight: 800

        &:hover
            color: var(--indigo-d)

.auth-legal-fine
    margin-top: -4px
    color: #5D6578
    font-size: 10.8px
    line-height: 1.42
    font-weight: 500

    a
        color: #174AEF
        text-decoration: underline
        text-underline-offset: 3px
        text-decoration-thickness: 1px
        font-weight: 800

        &:hover
            color: var(--indigo-d)

.auth-back
    width: 100%
    min-height: 44px
    display: inline-flex
    align-items: center
    justify-content: center
    gap: 9px
    border: 1.5px solid var(--line)
    border-radius: 14px
    background: #fff
    color: #687087
    font-family: 'Geologica', sans-serif
    font-size: 13px
    font-weight: 900
    cursor: pointer
    padding: 12px 18px
    box-shadow: 0 8px 18px rgba($dark-new, .05)
    transition: background .16s, border-color .16s, color .16s, transform .16s, box-shadow .16s

    &:hover
        background: var(--indigo-tint)
        border-color: rgba($main-new, .28)
        color: var(--indigo)
        transform: translateY(-1px)
        box-shadow: 0 10px 22px rgba($main-new, .09)
        text-decoration: none

    .v-icon
        color: inherit !important

.auth-error
    display: block
    margin-top: 6px
    color: var(--danger)
    font-size: 11.5px
    font-weight: 700
    line-height: 1.35

.auth-error--check
    margin-left: 28px
    margin-top: 0

.auth-message
    display: none
    border-radius: 14px
    background: var(--indigo-tint)
    border: 1px solid rgba($main-new, .16)
    color: var(--indigo-d)
    font-size: 12px
    font-weight: 800
    line-height: 1.4
    padding: 12px 14px
    margin-top: 0

    &.is-visible
        display: block

.auth-google-note
    margin-top: 18px
    padding: 14px 16px
    border-radius: 14px
    background: #fff
    border: 1.5px solid rgba($alert-new, .36)
    box-shadow: 0 8px 20px rgba($dark-new, .06)
    display: flex
    align-items: flex-start
    gap: 10px

.auth-google-note-icon
    width: 24px
    height: 24px
    border-radius: 8px
    background: #FFE8ED
    color: var(--danger)
    display: flex
    align-items: center
    justify-content: center
    flex: 0 0 24px
    font-family: 'Geologica', sans-serif
    font-size: 13px
    font-weight: 900
    line-height: 1
    margin-top: 1px

.auth-google-note-text
    font-size: 12px
    line-height: 1.45
    color: #5D6578 !important
    font-weight: 500

    strong
        display: block
        color: $alert-new !important
        font-family: 'Geologica', sans-serif
        font-size: 12px
        font-weight: 900
        margin-bottom: 4px
        letter-spacing: -.01em

    a
        color: $alert-new !important
        font-weight: 900
        text-decoration: underline
        text-underline-offset: 3px
        text-decoration-thickness: 1px

        &:hover
            color: darken($alert-new, 12%) !important

@media (max-width: 560px)
    .auth-backdrop
        padding: 12px
        align-items: flex-start
        padding-top: 24px

    .auth-close
        right: 12px
        top: 12px
        width: 34px
        height: 34px
        border-radius: 11px

    .auth-card
        width: 100%
        border-radius: 22px
        max-height: calc(100dvh - 36px)

    .auth-hero
        padding: 20px 16px 16px

    .auth-brand-row
        gap: 10px
        padding-right: 40px
        min-height: 48px

    .auth-brand-logo
        width: 136px
        max-height: 40px

    .auth-hero-title
        font-size: 21px
        margin-top: 16px
        max-width: 300px

    .auth-hero-title--promo
        font-size: 18px
        max-width: none

    .auth-hero-sub
        font-size: 12.2px
        line-height: 1.45

    .auth-tabs
        height: 42px
        margin-top: 14px

    .auth-body
        padding: 18px 16px

    .auth-form
        gap: 16px

    .auth-field
        height: 52px
        border-radius: 14px

    .auth-check
        font-size: 12.6px
        gap: 9px

    .auth-consent-title
        font-size: 12.2px

    .auth-legal-fine
        font-size: 10.5px

    .auth-google-note
        margin-top: 16px
        padding: 12px
        border-radius: 14px
</style>
